import { api_configuration } from "../common";
import api_cs = api_configuration.types.client_state.alan_api;

type ScalarObserver<T> = (value: T, subscription: api_cs.Subscription) => void;

export class ObservableType<T> {
	protected val: T;
	private observers = new Map<ScalarObserver<T>, api_cs.Subscription>();
	constructor(val: T) {
		this.val = val;
	}
	get value(): T {
		return this.val;
	}
	get observerCount(): number {
		return this.observers.size;
	}
	update(val: T): T {
		if (this.val !== val) {
			this.val = val;
			for (const [observer, subscription] of this.observers) {
				observer(this.val, subscription);
			}
		}
		return this.val;
	}
	subscribe(observer: ScalarObserver<T>, init = true): api_cs.Subscription {
		const subscription: api_cs.Subscription = {
			unsubscribe: () => this.observers.delete(observer),
		};
		this.observers.set(observer, subscription);
		if (init) observer(this.value, subscription);
		return subscription;
	}
	subscribeConditional(condition: (val: T) => boolean, observer: ScalarObserver<boolean>): api_cs.Subscription {
		let conditional = condition(this.value);
		const subscription = this.subscribe((new_value, subscription) => {
			const new_conditional = condition(new_value);
			if (conditional !== new_conditional) {
				observer(new_conditional, subscription);
				conditional = new_conditional;
			}
		}, false);
		observer(conditional, subscription);
		return subscription;
	}
	async await(condition: (val: T) => boolean): Promise<void> {
		if (condition(this.value)) return;
		return new Promise<void>((resolve) => {
			this.subscribe((new_value, subscription) => {
				if (condition(new_value) === true) {
					subscription.unsubscribe();
					resolve();
				}
			});
		});
	}
}

export class ObservableNumber extends ObservableType<number> {
	add(n: number): void {
		this.update(this.value + n);
	}
	subtract(n: number): void {
		this.update(this.value - n);
	}
	increment(): void {
		this.add(1);
	}
	decrement(): void {
		this.subtract(1);
	}
}

export class ObservableSet<T> {
	private set: Set<T>;
	size: ObservableType<number>;
	constructor(iterable?: Iterable<T> | null | undefined) {
		this.set = new Set<T>(iterable);
		this.size = new ObservableType<number>(this.set.size);
	}
	add(value: T): this {
		this.set.add(value);
		this.size.update(this.set.size);
		return this;
	}
	delete(value: T): boolean {
		const result = this.set.delete(value);
		this.size.update(this.set.size);
		return result;
	}
	has(value: T): boolean {
		return this.set.has(value);
	}
	clear(): void {
		this.set.clear();
		this.size.update(this.set.size);
	}
	[Symbol.iterator](): IterableIterator<T> {
		return this.set.values();
	}
	entries(): IterableIterator<[T, T]> {
		return this.set.entries();
	}
	keys(): IterableIterator<T> {
		return this.set.keys();
	}
	values(): IterableIterator<T> {
		return this.set.values();
	}
	forEach(callbackfn: (value: T, value2: T, set: Set<T>) => void, thisArg?: any): void {
		return this.set.forEach(callbackfn, thisArg);
	}
}

export class ObservableMap<K, T> {
	private map: Map<K, T>;
	size: ObservableType<number>;
	constructor() {
		this.map = new Map<K, T>();
		this.size = new ObservableType<number>(this.map.size);
	}
	set(key: K, value: T): this {
		this.map.set(key, value);
		this.size.update(this.map.size);
		return this;
	}
	delete(key: K): boolean {
		const result = this.map.delete(key);
		this.size.update(this.map.size);
		return result;
	}
	has(key: K): boolean {
		return this.map.has(key);
	}
	clear(): void {
		this.map.clear();
		this.size.update(this.map.size);
	}
	[Symbol.iterator](): IterableIterator<[K, T]> {
		return this.map.entries();
	}
	entries(): IterableIterator<[K, T]> {
		return this.map.entries();
	}
	keys(): IterableIterator<K> {
		return this.map.keys();
	}
	values(): IterableIterator<T> {
		return this.map.values();
	}
	forEach(callbackfn: (value: T, key: K, map: Map<K, T>) => void, thisArg?: any): void {
		return this.map.forEach(callbackfn, thisArg);
	}
}
